import type { ValueFormat } from "@/types";

/**
 * The two formatters that can't be resolved without React context — currency
 * comes from the company account's settings and dates from its timezone. They're
 * passed in so this file stays React-free (see `useValueFormat` for the bound
 * version).
 */
export interface ValueFormatters {
  money: (value: number | string | null | undefined) => string;
  date: (
    value: string | null | undefined,
    options?: { withTime?: boolean; pattern?: string },
  ) => string;
}

const decimal = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

const whole = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

/**
 * Render a raw widget value according to the `ValueFormat` the backend declared.
 *
 * Widgets never send formatted strings — that's what keeps currency and timezone
 * decisions on the client, where the company's settings actually live.
 */
export function formatValue(
  value: number | string | null | undefined,
  format: ValueFormat,
  formatters: ValueFormatters,
): string {
  if (value === null || value === undefined || value === "") return "—";

  switch (format) {
    case "currency":
      return formatters.money(value);

    case "date":
      return formatters.date(String(value));

    case "month":
      // Categories arrive as 'YYYY-MM'; widen to a real date so the datetime
      // layer can parse it, then show month + year only.
      return formatters.date(`${String(value)}-01`, { pattern: "LLL yyyy" });

    case "percent": {
      const numeric = Number(value);
      return Number.isFinite(numeric)
        ? `${decimal.format(numeric)}%`
        : String(value);
    }

    case "duration":
      return formatDuration(Number(value));

    case "integer": {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? whole.format(numeric) : String(value);
    }

    case "number": {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? decimal.format(numeric) : String(value);
    }

    case "text":
    default:
      return String(value);
  }
}

/** Seconds -> "2h 15m" / "45m" / "30s". */
function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return "—";

  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${total}s`;
}
